// The generate form's drafts, read from the catalog (G1: the SPA renders its forms from
// `GET /v1/catalog`), and their one conversion to a `JobRequest`, checked by the generated schema
// of the chosen type: each type's fields and schemas are its module's (`types/<type>.ts`, spec F9).
// The quote sends the same body without the prompt, which the price never reads (live prices
// before a prompt is written), so the price is the start's. The draft's state is `draft-state.ts`.
import type {
  ActorSource,
  Catalog,
  CatalogImageModel,
  CatalogJobType,
  CatalogVideoModel,
  ExampleRef,
  GenerationType,
  Job,
  AdvancedSettings,
  Element,
  JobQuoteRequest,
  Shot,
  SourceRef,
  TemplateSource,
} from "../../api/generated/model";
import type { Frames, GenerateDraft } from "./draft-state";
import { referenceCap, trimImages } from "./image/references";
import { JOB_TYPES } from "./types";
import { NO_ADVANCED } from "./video/advanced";
import type { ElementDraft } from "./video/elements";
import { VIDEO_TYPES, videoTypeOf } from "./video/modes";
import { firstShots, fitShots, multiShotOf, shortestOf } from "./video/shots";

export type CatalogModel = CatalogImageModel | CatalogVideoModel;

/**
 * Whether an Actions tool makes jobs of this type (#209): its form, not the inputs, picks the
 * type, so live's narrowing leaves it alone, and the job works on a video (`sourceVideo`).
 */
export function isToolType(type: GenerationType): boolean {
  return JOB_TYPES[type].source === "tool";
}

/** Live's word for a job's type in its generation lists (the type module's `label`). */
export function typeLabel(type: GenerationType): string {
  return JOB_TYPES[type].label;
}

/** The detail's title: live's image preview titles, else the type's list word. */
export function detailTitle(type: GenerationType): string {
  return JOB_TYPES[type].detailTitle ?? JOB_TYPES[type].label;
}

export function modelsFor(catalog: Catalog, type: CatalogJobType): CatalogModel[] {
  // Sound has its own page and forms (G7-sound-types), ad copies their modal (G8): no Studio
  // model makes either.
  if (type.medium === "audio" || type.medium === "text") return [];
  const models: CatalogModel[] =
    type.medium === "image" ? catalog.image_models : catalog.video_models;
  return models.filter((model) => model.job_types.includes(type.id));
}

/** Live's image composer starts at 3 outputs, of 1 to 5. */
export const DEFAULT_IMAGE_OUTPUTS = 3;

/** The model a new draft starts on: the catalog's default (live's), else the first. */
export function defaultModel(models: CatalogModel[]): CatalogModel {
  return models.find((model) => model.is_default) ?? models[0]!;
}

export type Medium = "image" | "video";

/**
 * The types the form offers, with a source or without, that have a model in the catalog; on the
 * studio's Image or Video page, only that medium's.
 */
export function typesFor(
  catalog: Catalog,
  withSource: boolean,
  medium?: Medium,
): CatalogJobType[] {
  // The composer's own types: from text, or from a delivered image (the source).
  const source = withSource ? "image" : "text";
  return catalog.job_types.filter(
    (type) =>
      type.needs_source === withSource &&
      JOB_TYPES[type.id].source === source &&
      (medium === undefined || type.medium === medium) &&
      modelsFor(catalog, type).length > 0,
  );
}

/** Whether a model takes an end frame: any of its video blocks does (G6, live's visibility). */
export function takesEndFrame(model: CatalogModel): boolean {
  return (
    model.medium === "video" &&
    VIDEO_TYPES.some((type) => model.modes[type]?.end_frame === true)
  );
}

/** The video types the catalog offers, which the composer can send. */
export function offeredVideoTypes(catalog: Catalog): GenerationType[] {
  return catalog.job_types
    .filter((type) => type.medium === "video" && VIDEO_TYPES.includes(type.id))
    .map((type) => type.id);
}

/**
 * Whether a job of this type makes sound by default (G6, live's sound rule): on where the type's
 * block has the switch (live's composer starts with sound on) or always makes sound, off where it
 * makes silent video only. The API refuses off on `always` and on on `none`.
 */
function soundOf(model: CatalogModel, type: GenerationType): boolean {
  const audio = model.medium === "video" ? model.modes[type]?.audio : undefined;
  return audio === "toggle" || audio === "always";
}

/** Whether the user may turn the sound on and off for this type (the block's `toggle`). */
function soundToggles(model: CatalogModel, type: GenerationType): boolean {
  return model.medium === "video" && model.modes[type]?.audio === "toggle";
}

/**
 * A model's defaults for a type, keeping the prompt, the source and the end frame. A model that
 * takes no end frame keeps it out of sight and unsent, as live's form does, so a model that
 * takes one shows it again.
 */
export function draftFor(
  type: CatalogJobType,
  model: CatalogModel,
  prompt = "",
  source: SourceRef | null = null,
  endFrame: SourceRef | null = null,
): GenerateDraft {
  const video = model.medium === "video" ? model : undefined;
  // The end frame this model can use: it decides the ratio; the draft keeps the frame anyway.
  const end = takesEndFrame(model) ? endFrame : null;
  return {
    type: type.id,
    model: model.id,
    prompt,
    // Live's composer starts an image at 3 outputs (its DEFAULT_IMAGES_COUNT) and a video at 1.
    outputs: model.medium === "image" ? Math.min(DEFAULT_IMAGE_OUTPUTS, maxOutputs(type, model)) : 1,
    // With a frame, live's ratio is "auto": the video keeps the frame's shape. A tool's job
    // takes no frame, so its ratio is the model's own (live's "auto" where it keeps the source's).
    aspectRatio:
      isToolType(type.id) || (source === null && end === null) ? model.aspect_ratios.default : null,
    durationSeconds: video?.durations?.default ?? null,
    resolution: video?.resolutions?.default ?? null,
    // Live's composer starts with sound on, and turns it on again with each model it picks.
    audio: soundOf(model, type.id),
    source,
    sourceProduct: null,
    endFrame,
    middleFrames: [],
    references: [],
    elements: [],
    videos: [],
    audios: [],
    multiShot: false,
    shots: firstShots(multiShotOf(model, type.id), shortestOf(model, type.id)),
    advanced: NO_ADVANCED,
    sourceVideo: null,
    keepSourceAudio: false,
  };
}

/** The most outputs one job may ask for: the model's cap and the type's (G1 limits). */
export function maxOutputs(type: CatalogJobType, model: CatalogModel): number {
  return Math.min(type.max_outputs, model.max_outputs);
}

/**
 * The draft with these frames (G1 decision 21, #228): a frame turns text into its pair from an
 * image, as live picks the first mode that takes every filled input, and the last frame going
 * turns it back. The prompt, the model, the count, a video's duration, resolution and sound, and
 * (when the frames go) a chosen ratio stay where the catalog allows. `undefined` when the catalog
 * offers no such type on this page.
 */
export function withFrames(
  catalog: Catalog,
  draft: GenerateDraft,
  medium: Medium,
  frames: Frames,
): GenerateDraft | undefined {
  const framed =
    frames.source !== null || frames.endFrame !== null || frames.references.length > 0;
  // A tool's form keeps its type whatever the inputs (`isToolType`).
  const type = isToolType(draft.type)
    ? catalog.job_types.find((each) => each.id === draft.type)
    : (videoTypeFor(catalog, draft, medium, frames) ?? pairedType(catalog, draft, medium, framed));
  if (type === undefined) return undefined;
  const models = modelsFor(catalog, type);
  const model = models.find((each) => each.id === draft.model) ?? defaultModel(models);
  const next = draftFor(type, model, draft.prompt, frames.source, frames.endFrame);
  const video = model.medium === "video" ? model : undefined;
  // An end frame the model takes none of is kept, but frames nothing.
  const holdsFrame =
    !isToolType(type.id) &&
    (frames.source !== null ||
      (frames.endFrame !== null && takesEndFrame(model)) ||
      frames.references.length > 0);
  return {
    ...next,
    outputs: Math.min(draft.outputs, maxOutputs(type, model)),
    // With a frame the job keeps the image's ratio; a ratio chosen with it stays when it goes.
    aspectRatio: holdsFrame
      ? next.aspectRatio
      : (offered(draft.aspectRatio, model.aspect_ratios.values) ?? next.aspectRatio),
    durationSeconds:
      offered(draft.durationSeconds, video?.durations?.values) ?? next.durationSeconds,
    resolution: offered(draft.resolution, video?.resolutions?.values) ?? next.resolution,
    audio: soundToggles(model, type.id) ? draft.audio : next.audio,
    ...shotsFor(draft, model, type.id),
    advanced: draft.advanced,
    // The product goes with its photo: no source, no product.
    sourceProduct: frames.source === null ? null : frames.sourceProduct,
    references: frames.references,
    middleFrames: draft.middleFrames,
    elements: draft.elements,
    sourceVideo: draft.sourceVideo,
    keepSourceAudio: draft.keepSourceAudio,
    videos: draft.videos,
    audios: draft.audios,
  };
}

/**
 * A video's type for these inputs, on the draft's model: live's narrowing (`video/modes.ts`),
 * the first of text, image and reference to video that takes them all.
 */
function videoTypeFor(
  catalog: Catalog,
  draft: GenerateDraft,
  medium: Medium,
  frames: Frames,
): CatalogJobType | undefined {
  if (medium !== "video") return undefined;
  const model = catalog.video_models.find((each) => each.id === draft.model);
  if (model === undefined) return undefined;
  const filled = {
    start: frames.source !== null,
    end: frames.endFrame !== null,
    middle: draft.middleFrames.length,
    references: frames.references.length,
    videos: draft.videos.length,
    audios: draft.audios.length,
    elements: draft.elements.length,
    shots: draft.multiShot,
  };
  const id = videoTypeOf(model, filled, offeredVideoTypes(catalog));
  return catalog.job_types.find((each) => each.id === id);
}

/** An image's type: from text or from an image, the pair of the draft's (G1 decision 21). */
function pairedType(
  catalog: Catalog,
  draft: GenerateDraft,
  medium: Medium,
  framed: boolean,
): CatalogJobType | undefined {
  const types = typesFor(catalog, framed, medium);
  return (
    // From text and from an image pair up, so adding or removing a source keeps the kind of job.
    types.find((each) => each.id === JOB_TYPES[draft.type].pair) ??
    types.find((each) => each.id === draft.type) ??
    types[0]
  );
}

/**
 * The switch and the shots on another model or type: kept, trimmed to its caps, where it makes
 * multi-shot video; off where it does not (live's form reconciliation).
 */
export function shotsFor(
  draft: Pick<GenerateDraft, "multiShot" | "shots">,
  model: CatalogModel,
  type: GenerationType,
): Pick<GenerateDraft, "multiShot" | "shots"> {
  const caps = multiShotOf(model, type);
  if (caps === null) return { multiShot: false, shots: firstShots(null) };
  return {
    multiShot: draft.multiShot,
    shots: fitShots(draft.shots, caps, shortestOf(model, type)),
  };
}

/** The value when the catalog still offers it, else null. */
function offered<T>(value: T | null | undefined, values: readonly T[] | undefined): T | null {
  return value !== null && value !== undefined && values?.includes(value) === true ? value : null;
}

/**
 * The one source a cloned job's draft starts from (G6): a tweak's first reference, or a video's
 * start frame. A reference video's images wait for the composer's several references (#232).
 */
export function cloneSource(job: Job, kept: Kept = KEEP_ALL): SourceRef | null {
  const first = (job.params?.references ?? []).find((ref) => keptRef(ref, kept));
  const frame = job.params?.start_frame;
  const start = frame === null || frame === undefined || !keptRef(frame, kept) ? null : studioRef(frame);
  return (first === undefined ? null : studioRef(first)) ?? start;
}

/** Whether a clone takes a library file back: false once it is gone (deleted, in the trash). */
export type Kept = (assetId: string) => boolean;

const KEEP_ALL: Kept = () => true;

/** A job's input is kept unless it is a library file the clone no longer has. */
function keptRef(ref: { kind: string; asset_id?: string }, kept: Kept): boolean {
  return ref.kind !== "asset" || ref.asset_id === undefined || kept(ref.asset_id);
}

/**
 * Every library file a clone of `job` takes back: its references, frames, a tool's video,
 * reference videos and audios and its elements' parts. The Studio reads them before the composer opens (issue 828,
 * item 23), for their pictures and to leave out the ones gone since.
 */
export function cloneAssetIds(job: Job): string[] {
  const params = job.params;
  if (params === null) return [];
  const refs: ({ kind: string; asset_id?: string } | null | undefined)[] = [
    ...(params.references ?? []),
    params.start_frame,
    params.end_frame,
    ...(params.middle_frames ?? []),
    params.source_video,
    ...(params.reference_videos ?? []),
    ...(params.reference_audios ?? []),
    ...(params.elements ?? []).flatMap((element) => [element.frontal, ...(element.images ?? []), element.video]),
  ];
  const ids = refs.flatMap((ref) => (ref?.kind === "asset" && ref.asset_id !== undefined ? [ref.asset_id] : []));
  return [...new Set(ids)];
}

/**
 * A reference a Studio draft holds: a job's output or a library file. An actor's portrait is an
 * Advanced Talking Head's keyframe's (#223), and a card's example input a template run's step's
 * (G14), never a Studio draft's, so a clone leaves them out.
 */
function studioRef(ref: ActorSource | ExampleRef): SourceRef | null {
  return ref.kind === "example" || "actor_id" in ref ? null : ref;
}

/**
 * A draft from an earlier job (`?clone=`, design-system.md 3.3): its model, its params and its
 * count of outputs where the catalog still offers them, the model's defaults where it does not.
 */
export function cloneDraft(
  catalog: Catalog,
  type: CatalogJobType,
  job: Job,
  source: SourceRef | null,
  kept: Kept = KEEP_ALL,
): GenerateDraft {
  const models = modelsFor(catalog, type);
  const model = models.find((each) => each.id === job.model) ?? defaultModel(models);
  const params = job.params;
  const endFrame = params?.end_frame ?? null;
  const base = draftFor(type, model, params?.prompt ?? "", source, endFrame !== null && keptRef(endFrame, kept) ? endFrame : null);
  if (params === null) return base;
  // A tweak's first kept reference is its source (`cloneSource`); the rest are its references.
  // A library file gone since is left out: the server would refuse the draft (issue 828, item 23).
  const keptRefs = (params.references ?? []).filter((ref) => keptRef(ref, kept));
  const held =
    type.id === "image.tweak"
      ? keptRefs.slice(1).flatMap((ref) => studioRef(ref) ?? [])
      : [];
  const { references } = trimImages({ source, references: held }, referenceCap(model));
  const video = model.medium === "video" ? model : undefined;
  return {
    ...base,
    outputs: Math.min(Math.max(job.outputs.length, 1), maxOutputs(type, model)),
    aspectRatio: offered(params.aspect_ratio, model.aspect_ratios.values) ?? base.aspectRatio,
    durationSeconds:
      offered(params.duration_seconds, video?.durations?.values) ?? base.durationSeconds,
    resolution: offered(params.resolution, video?.resolutions?.values) ?? base.resolution,
    audio: soundToggles(model, type.id) ? (params.generate_audio ?? base.audio) : base.audio,
    ...clonedShots(params.shots, model, type.id, base),
    references,
    // A tool's video (Video Edit, Video Extend), where it is still in the library, and its Keep
    // Reference Audio (issue 828, item 23); the composer sends the switch only to a model that
    // takes it.
    sourceVideo:
      params.source_video !== null && params.source_video !== undefined && keptRef(params.source_video, kept)
        ? studioRef(params.source_video)
        : null,
    keepSourceAudio: params.keep_source_audio ?? base.keepSourceAudio,
    // A clone's keyframes, elements, videos and audios, their pictures read by the composer.
    middleFrames: (params.middle_frames ?? []).flatMap((ref) =>
      ref.kind === "asset" && kept(ref.asset_id) ? [{ assetId: ref.asset_id, url: null, name: "A keyframe" }] : [],
    ),
    elements: (params.elements ?? []).map((element) => clonedElement(element, kept)),
    // The composer sends library files only, so an earlier job's output is not taken back here.
    videos: (params.reference_videos ?? []).flatMap((ref) =>
      ref.kind === "asset" && kept(ref.asset_id) ? [{ assetId: ref.asset_id, url: null, name: "A reference video" }] : [],
    ),
    audios: (params.reference_audios ?? []).flatMap((ref) =>
      kept(ref.asset_id) ? [{ assetId: ref.asset_id, url: null, name: "A reference audio" }] : [],
    ),
  };
}

/**
 * What a clone says when its job's model is no longer offered (#252 item 1): the draft is on the
 * type's default instead (`cloneDraft`), and the user should see that rather than meet it later.
 * Live has no words for it; ours name both models. Null when the job's model is still offered.
 */
export function cloneModelNotice(catalog: Catalog, type: CatalogJobType, job: Job): string | null {
  const models = modelsFor(catalog, type);
  if (models.length === 0 || models.some((each) => each.id === job.model)) return null;
  return `Made with ${job.model}, now on ${defaultModel(models).name}: that model is no longer offered.`;
}

/** A cloned job's shots, with the switch on, where the model still makes multi-shot video. */
function clonedShots(
  shots: Shot[] | null | undefined,
  model: CatalogModel,
  type: GenerationType,
  base: GenerateDraft,
): Pick<GenerateDraft, "multiShot" | "shots"> {
  if (shots === null || shots === undefined || shots.length === 0) {
    return { multiShot: base.multiShot, shots: base.shots };
  }
  const held = shots.map((shot) => ({
    prompt: shot.prompt ?? "",
    durationSeconds: shot.duration_seconds,
  }));
  return shotsFor({ multiShot: true, shots: held }, model, type);
}

/** The start's body; the schema of the draft's type checks it, a written prompt included. */
export function jobRequestOf(draft: GenerateDraft, advanced: AdvancedSettings | null = null) {
  const module = JOB_TYPES[draft.type];
  // Shots carry their own prompts, each one needed to start (a quote may leave them out).
  const fields = module.fields(draft, advanced);
  const body = draft.multiShot ? fields : { ...fields, prompt: draft.prompt.trim() };
  return module.request.safeParse(body);
}

/**
 * The quote's body: the start's without the prompt, which the price never reads (live sends
 * none either), or `undefined` while the draft cannot be priced.
 */
export function quoteRequestOf(
  draft: GenerateDraft,
  advanced: AdvancedSettings | null = null,
): JobQuoteRequest | undefined {
  // Nor the negative prompt's words, which the price never reads either.
  const settings = advanced === null ? null : withoutNegativeWords(advanced);
  const parsed = JOB_TYPES[draft.type].quote.safeParse(JOB_TYPES[draft.type].fields(draft, settings));
  if (!parsed.success) return undefined;
  // An Advanced body has no prompt to leave out.
  if (!("prompt" in parsed.data)) return parsed.data;
  const { prompt: _unread, ...priced } = parsed.data;
  // Nor a shot's prompt: only the lengths are priced.
  if ("shots" in priced && priced.shots !== null && priced.shots !== undefined) {
    return {
      ...priced,
      shots: priced.shots.map((shot) => ({ duration_seconds: shot.duration_seconds })),
    };
  }
  return priced;
}

/** The settings without the negative prompt's words, or null when nothing else is left. */
function withoutNegativeWords(advanced: AdvancedSettings): AdvancedSettings | null {
  const { negative_prompt: _words, ...rest } = advanced;
  return Object.keys(rest).length > 0 ? rest : null;
}

/** An element of an earlier job: its parts' ids, with plain names, those still in the library. */
function clonedElement(element: Element, kept: Kept): ElementDraft {
  const picked = (ref: SourceRef | TemplateSource | null | undefined, name: string) =>
    ref?.kind === "asset" && kept(ref.asset_id) ? { assetId: ref.asset_id, url: null, name } : null;
  return {
    frontal: picked(element.frontal, "The frontal image"),
    images: (element.images ?? []).flatMap((ref) => {
      const image = picked(ref, "A reference image");
      return image === null ? [] : [image];
    }),
    video: picked(element.video, "The video"),
    // The voice it spoke with: the start refuses one deleted since (G2-kling-element-voice-picker).
    voice:
      element.voice_id === null || element.voice_id === undefined
        ? null
        : { id: element.voice_id, name: null, kind: null },
  };
}
